import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import 'primereact/resources/themes/lara-light-cyan/theme.css';
import { Skeleton } from 'primereact/skeleton';
import React, { useCallback, useEffect, useRef } from 'react';
import Heading from '../../../generic-components/Heading';
import { useClearQueryString } from '../../../hooks/utilities/useClearQueryString';
import { getUrlParamValueByKey } from '../../../services/getUrlParamValueByKey';
import { useStore } from '../../../store';
import { TransactionStatus } from '../enum/transaction-status';
import { TransactionContent } from '../hooks/use-transactions';
import { useDates } from '../hooks/useDates';
import { mockLoadingTable } from '../mock/mock_data';
import { enrichData } from '../services';
import '../styles/transction-table.css';
import { LiliTransaction } from '../types/lili-transaction';
import { getTransactionById } from '../utils/transactions.helpers';
import { EContentTypes } from './TransactionDetailsDrawer';
import { PagingTrigger } from './cell-templates/paging-trigger';
import { amountTemplate } from './cell-templates/transaction-amount';
import { categoryTemplate } from './cell-templates/transaction-category';
import { descriptionTemplate } from './cell-templates/transaction-description';
import { transactionStatus } from './cell-templates/transaction-status';

interface IProps {
  isLoading: boolean;
  shouldLockScroll?: boolean;
  disableGrouping?: boolean;
}
export const TransactionTable = ({ isLoading, shouldLockScroll, disableGrouping }: IProps) => {
  const transactionsList = useStore((state) => state.transactionsList);
  const setSelectedTransaction = useStore((state) => state.setSelectedTransaction);
  const setDrawerContentType = useStore((state) => state.setDrawerContentType);
  const selectedTransaction = useStore((state) => state.selectedTransaction);
  const searchParams = useStore((state) => state.searchParams);
  const setSearchParams = useStore((state) => state.setSearchParams);
  const elTableWrapper = useRef<HTMLDivElement>(null);
  const { formatDateForTriggerTitle } = useDates();
  const { clearQueryString } = useClearQueryString();

  const selectAndOpenTransaction = async (transaction: LiliTransaction) => {
    setDrawerContentType(EContentTypes.DETAILS);
    const trxData = await enrichData(transaction);
    setSelectedTransaction(trxData);
  };
  const handleTransactionSelect = async (e: any) => {
    if (isLoading) return;
    await selectAndOpenTransaction(e.value);
    if (selectedTransaction?.transactionId !== e.value.transactionId) clearQueryString();
  };

  // eslint-disable-next-line consistent-return
  const formatHeader = (status: string) => {
    if (status === TransactionStatus.PENDING) {
      return 'Pending';
    }
    if (status === TransactionStatus.SETTLED) {
      return 'Completed';
    }
  };

  const headerTemplate = (transactionData: TransactionContent) => (
    <div className='flex align-items-center'>
      <span className='font-metricRSemibold text-[12px] text-bw07'>{formatHeader(transactionData?.status || '')}</span>
    </div>
  );

  const dateTemplate = (transactionData: LiliTransaction) => (
    <Heading
      title={formatDateForTriggerTitle(new Date(transactionData.timestamp))}
      className='font-metricR text-[14px] text-bw08'
    />
  );

  const loadingTemplate = () => (
    <div
      id='loadingCellTemplate'
      className='flex align-items-center'
      style={{ height: '17px', flexGrow: '1', overflow: 'hidden' }}
    >
      <Skeleton width='100%' height='1rem' />
    </div>
  );

  const updatedTransactions = useCallback(() => [...transactionsList], [transactionsList]);
  useEffect(() => {
    const transactionId = getUrlParamValueByKey('id', searchParams) || '';
    const transaction = getTransactionById(transactionsList, transactionId);
    if (transaction) {
      selectAndOpenTransaction(transaction);
      setSearchParams('');
    }
  }, [transactionsList]);

  const commonStyle = {
    fontFamily: 'metric-R-semibold',
    fontSize: '14px',
    borderBottom: disableGrouping ? '1px solid #e5e7eb' : '',
  };

  return (
    <div ref={elTableWrapper} className='grow mr-9 relative'>
      <div
        className={`absolute inset-0 ${shouldLockScroll ? 'overflow-y-hidden' : 'overflow-y-scroll'} no-scrollbar no-scrollbar::-webkit-scrollbar`}
      >
        <DataTable
          id='data-table-prime-react'
          value={isLoading ? mockLoadingTable : updatedTransactions()}
          selectionMode='single'
          selection={selectedTransaction}
          onSelectionChange={handleTransactionSelect}
          tableStyle={{ minWidth: '50rem' }}
          rowGroupMode={disableGrouping ? undefined : 'subheader'}
          groupRowsBy='status'
          scrollable
          emptyMessage='No results found'
          // @ts-ignore
          scrollHeight={elTableWrapper?.current?.clientHeight || '100%'}
          rowGroupHeaderTemplate={headerTemplate}
        >
          <Column
            style={{
              width: '150px',
              ...commonStyle,
            }}
            field='date'
            header='Date'
            body={isLoading ? loadingTemplate : dateTemplate}
          />
          <Column
            style={{ width: '320px', ...commonStyle }}
            field='name'
            header='Description'
            alignHeader='left'
            body={isLoading ? loadingTemplate : descriptionTemplate}
          />
          <Column
            style={{ width: '340px', ...commonStyle }}
            field='category'
            header='Category'
            body={(data) => <div className=''>{categoryTemplate(data, isLoading)}</div>}
          />
          <Column
            style={{ width: '150px', ...commonStyle }}
            field='status'
            body={(data) => transactionStatus(data, isLoading)}
          />
          <Column
            style={{
              width: '136px',
              ...commonStyle,
            }}
            alignHeader='right'
            field='amount'
            header='Amount'
            body={isLoading ? loadingTemplate : amountTemplate}
          />
          <Column style={{ width: '80px', ...commonStyle }} body={PagingTrigger} />
        </DataTable>
      </div>
    </div>
  );
};
